// src/components/molecules/PeriodFilterButton/PeriodFilterButton.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Divider,
  FormControl,
  ListSubheader,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  useTheme
} from '@mui/material';
import {
  Create as CreateIcon,
  DateRange as DateRangeIcon,
  Event as EventIcon
} from '@mui/icons-material';
import DateRangePicker from '../DateRangePicker/DateRangePicker';
import {
  operationalFontSizes,
  operationalRadii,
  operationalSizing,
  operationalSpacing
} from '../../../design-system/tokens';
import { useTheme as useDesignTheme } from '../../../design-system/providers/ThemeProvider';

export const PERIOD_FILTER_OPTIONS = [
  { value: 'all', label: 'Todos', compactLabel: 'Todos' },
  { value: '24h', label: 'Últimas 24h', compactLabel: '24h' },
  { value: '3d', label: 'Últimos 3 dias', compactLabel: '3d' },
  { value: '7d', label: 'Últimos 7 dias', compactLabel: '7d' },
  { value: '30d', label: 'Últimos 30 dias', compactLabel: '30d' },
  { value: 'custom', label: 'Personalizado...', compactLabel: 'Pers.' }
];

/**
 * PeriodFilterButton - Molécula de filtro rápido de período.
 *
 * Centraliza o botão/select de período usado em toolbars para manter
 * comportamento e visual dentro do Atomic Design System.
 */
const PeriodFilterButton = ({
  value = 'all',
  onChange,
  customDateRange = null,
  quickDateType = 'created',
  onQuickDateTypeChange,
  options = PERIOD_FILTER_OPTIONS,
  className = '',
  width = 104,
  size = 'small',
  ...props
}) => {
  const theme = useTheme();
  const { primaryGradient } = useDesignTheme();
  const [showCustomModal, setShowCustomModal] = useState(false);

  const handleQuickDateTypeChange = (event, nextType) => {
    if (!nextType) return;
    onQuickDateTypeChange?.(nextType);
  };

  const handleSelectChange = (event) => {
    const nextValue = event.target.value;
    if (!nextValue) return;

    if (nextValue === 'custom') {
      setShowCustomModal(true);
      return;
    }

    onChange(nextValue, null, quickDateType);
  };

  const handleCustomDateChange = (dateRange) => {
    setShowCustomModal(false);
    onChange('custom', dateRange);
  };

  const getDisplayLabel = (selectedValue) => {
    const option = options.find((item) => item.value === selectedValue);
    return option?.compactLabel || option?.label || selectedValue || 'Data';
  };

  return (
    <>
      <FormControl
        size={size}
        className={`period-filter-button ${className}`}
        sx={{ width, flexShrink: 0 }}
        {...props}
      >
        <Select
          value={value}
          onChange={handleSelectChange}
          displayEmpty
          renderValue={getDisplayLabel}
          startAdornment={
            <DateRangeIcon
              sx={{
                mr: 0.45,
                fontSize: '1.2rem',
                color: theme.palette.primary.contrastText
              }}
            />
          }
          sx={{
            height: operationalSizing.compact,
            background: primaryGradient || theme.palette.primary.main,
            borderRadius: `${operationalRadii.control}px`,
            minWidth: 0,
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              gap: operationalSpacing.xs,
              fontSize: operationalFontSizes.sm,
              fontWeight: 600,
              color: theme.palette.primary.contrastText,
              py: operationalSpacing.xs,
              pl: '10px !important',
              pr: '26px !important',
              overflow: 'hidden',
              textOverflow: 'clip',
              whiteSpace: 'nowrap'
            },
            '& .MuiSelect-icon': {
              color: theme.palette.primary.contrastText,
              right: 6
            },
            '&:hover': {
              filter: 'brightness(0.9)'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: `${theme.palette.primary.main} !important`,
              borderWidth: '2px !important'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: `${theme.palette.primary.main} !important`,
              borderWidth: '2px !important'
            }
          }}
          MenuProps={{
            PaperProps: { sx: { minWidth: 210 } }
          }}
        >
          <ListSubheader
            disableSticky
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            sx={{
              py: 1,
              px: 1.5,
              bgcolor: 'transparent !important',
              lineHeight: 'normal',
              cursor: 'default'
            }}
          >
            <ToggleButtonGroup
              value={quickDateType}
              exclusive
              onChange={handleQuickDateTypeChange}
              size="small"
              fullWidth
              sx={{
                '& .MuiToggleButton-root': {
                  py: 0.5,
                  fontSize: '0.72rem',
                  textTransform: 'none',
                  gap: 0.5,
                  lineHeight: 1.3
                }
              }}
            >
              <ToggleButton value="published">
                <EventIcon sx={{ fontSize: 14 }} />
                Publicação
              </ToggleButton>
              <ToggleButton value="created">
                <CreateIcon sx={{ fontSize: 14 }} />
                Cadastro
              </ToggleButton>
            </ToggleButtonGroup>
          </ListSubheader>
          <Divider sx={{ my: 0.5 }} />
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              onClick={option.value === 'custom' ? () => setShowCustomModal(true) : undefined}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {showCustomModal && (
        <DateRangePicker
          value={customDateRange}
          onChange={handleCustomDateChange}
          onClose={() => setShowCustomModal(false)}
          size="small"
          label="Período Personalizado"
        />
      )}
    </>
  );
};

PeriodFilterButton.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  customDateRange: PropTypes.shape({
    startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    dateType: PropTypes.string
  }),
  quickDateType: PropTypes.oneOf(['created', 'published']),
  onQuickDateTypeChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      compactLabel: PropTypes.string
    })
  ),
  className: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  size: PropTypes.oneOf(['small', 'medium'])
};

export default PeriodFilterButton;
